import { useState, useRef, useMemo, useEffect } from 'react';
import {
  Container,
  Stack,
  Title,
  Group,
  Alert,
  ActionIcon,
  Tooltip,
  Box,
  Text,
  Button,
  Paper,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconDownload } from '@tabler/icons-react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import {
  MetricTilesGrid,
  TILE_CONFIGS,
  tileLabelFor,
  AllHiddenNotice,
  DashboardEditControls,
  SortableItem,
  useDashboardDndSensors,
  buildDndAnnouncements,
  type HiddenItem,
  type DashboardEditControlsHandle,
} from '@/components/dashboard';
import { GettingStartedCard } from '@/components/dashboard/GettingStartedCard';
import { DashboardTasks } from '@/features/dashboard/DashboardTasks';
import { ChartControls } from '@/features/charts/ChartControls';
import { CHART_REGISTRY, chartLabelFor } from '@/features/charts/chartRegistry';
import { RefreshButton } from '@/features/charts/RefreshButton';
import { RefreshProgressModal } from '@/components/RefreshProgressModal';
import { AddAnnotationModal } from '@/components/charts/AddAnnotationModal';
import { useTank } from '@/contexts/TankContext';
import { buildApiUrl } from '@/api/config';
import { apiClient } from '@/api/client';
import { useDashboardData } from '@/hooks/useDashboardData';
import { useSparklineData } from '@/hooks/useSparklineData';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useTankParameters } from '@/hooks/useTankParameters';
import { useGettingStarted } from '@/hooks/useGettingStarted';
import { useExportImage } from '@/hooks/useExportImage';
import { useQueryClient } from '@tanstack/react-query';
import { useChartAnnotations } from '@/hooks/useChartAnnotations';
import { useDashboardLayout, type LayoutSection } from '@/hooks/useDashboardLayout';
import type { ChartAnnotation } from '@/types/chartAnnotation';

// Module scope — NOT inside the component: a per-render object would churn
// the layout hook's memo/callback identities every render.
const LAYOUT_DEFAULTS = {
  tileIds: TILE_CONFIGS.map(t => t.key),
  chartIds: CHART_REGISTRY.map(c => c.id),
};

export default function ChartsPage() {
  const { selectedTank } = useTank();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [dateRange, setDateRange] = useState<[Date, Date]>([
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    new Date(),
  ]);

  const [showMovingAverage, setShowMovingAverage] = useState(false);
  const [refreshJobId, setRefreshJobId] = useState<string | null>(null);
  const [progressModalOpened, setProgressModalOpened] = useState(false);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<'today' | 'week' | 'month'>('today');
  const [expandedCharts, setExpandedCharts] = useState<Record<string, boolean>>({});
  const toggleChart = (id: string) =>
    setExpandedCharts(prev => ({ ...prev, [id]: !prev[id] }));

  // Dashboard customization (edit mode + persisted layout)
  const [editMode, setEditMode] = useState(false);
  const controlsRef = useRef<DashboardEditControlsHandle>(null);
  // Current tank id, readable from long-lived closures (the Undo toast
  // callback below outlives the render that created it — a captured
  // `selectedTank` would go stale across a tank switch). Synced in an
  // effect (not during render) to respect concurrent-rendering semantics;
  // effect timing is sufficient because the ref is only read in async
  // toast callbacks, never during render.
  const selectedTankIdRef = useRef(selectedTank?.id);
  useEffect(() => {
    selectedTankIdRef.current = selectedTank?.id;
  }, [selectedTank?.id]);
  const sensors = useDashboardDndSensors();
  const [activeChartId, setActiveChartId] = useState<string | null>(null);
  const { layout, move, hide, show, reset, isCustomized } = useDashboardLayout(
    selectedTank?.id,
    LAYOUT_DEFAULTS
  );

  const toggleEditMode = (): void => {
    // Design decision 8: force-collapse so rectSortingStrategy always sees a
    // uniform chart grid while dragging is possible.
    if (!editMode) setExpandedCharts({});
    setEditMode(!editMode);
  };

  // Annotation modal state
  const [annotationModalOpened, setAnnotationModalOpened] = useState(false);
  const [selectedAnnotation, setSelectedAnnotation] = useState<ChartAnnotation | null>(null);
  const [annotationChartContext, setAnnotationChartContext] = useState<{
    parameter: string;
    parameterLabel: string;
    sources?: string[];
  } | null>(null);

  // Fetch tank parameters for dynamic UI rendering
  // This provides feature flags and enabled parameters for conditional rendering
  const { data: tankParams } = useTankParameters();

  // Zero-data hero: when the getting-started signal has settled (isReady) and no
  // data has arrived yet (the 'first-data' checklist item is not done), the
  // GettingStartedCard above becomes the hero and we hide the dead N/A tile grid
  // and the empty charts below rather than rendering a wall of empties. Gating on
  // isReady (all constituent queries settled) prevents the hero from flickering
  // in during initial loads; when data lands async (e.g. after a wizard device
  // connect) the query updates flip first-data done and the dashboard fills in.
  const gettingStarted = useGettingStarted();
  const hasFirstData =
    gettingStarted.items.find((item) => item.key === 'first-data')?.done ?? false;
  const isZeroData = gettingStarted.isReady && !hasFirstData;
  // The no-tank branch keeps MetricTilesGrid's own inline empty state (it already
  // covers the tiles); only the tank-with-no-data case hides the dead grid.
  //
  // BUT once the user dismisses the GettingStartedCard hero, hiding the grid too
  // would leave a near-empty dashboard with the best next-step CTAs gone. In that
  // case we keep the grid mounted so MetricTilesGrid's "No parameters enabled"
  // EmptyState (with its Log/Connect CTAs) becomes the actionable stand-in.
  const hideTileGrid = isZeroData && !!selectedTank && !gettingStarted.dismissed;

  // Zero-data means there is nothing customizable: charts are unmounted, and
  // the tile grid (when kept for the dismissed-hero case) only shows its
  // EmptyState. The Customize controls are hidden below whenever isZeroData —
  // this guard additionally forces edit mode OFF if it was active when the
  // dashboard became zero-data (e.g. the signal settled after the user clicked
  // Customize), so no stale edit chrome or hint lingers.
  useEffect(() => {
    if (isZeroData && editMode) setEditMode(false);
  }, [isZeroData, editMode]);

  // Consolidated dashboard data - replaces 12+ individual API calls with single request
  const {
    data: dashboardData,
    isLoading: dashboardLoading,
    error: dashboardError,
  } = useDashboardData({
    startDate: dateRange[0],
    endDate: dateRange[1],
    movingAverage: isMobile || showMovingAverage,  // Auto-enable on mobile OR when toggled
  });

  // 30-day sparkline data for metric tiles
  const { data: sparklineData } = useSparklineData();

  // Fetch chart annotations for the selected date range
  const { data: annotations = [] } = useChartAnnotations(selectedTank?.id, {
    startDate: dateRange[0],
    endDate: dateRange[1],
  });

  // Export dashboard as PNG
  const dashboardRef = useRef<HTMLDivElement>(null);
  const { exportElement, isExporting } = useExportImage();

  const handleExportDashboard = async () => {
    if (!dashboardRef.current) return;
    // Exit edit mode and wait two rAF ticks so React has committed AND the
    // browser has painted — otherwise edit chrome intermittently leaks into
    // the captured PNG.
    setEditMode(false);
    await new Promise<void>(resolve =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
    );
    try {
      await exportElement(dashboardRef.current, 'Dashboard');
      notifications.show({
        title: 'Dashboard exported',
        message: 'Dashboard saved as PNG',
        color: 'green',
        autoClose: 3000,
      });
    } catch {
      notifications.show({
        title: 'Export failed',
        message: 'Could not export dashboard. Please try again.',
        color: 'red',
      });
    }
  };

  // Extract data for child components
  const latestMetrics = dashboardData?.latest;
  const targetRanges = dashboardData?.target_ranges;

  // Tiles are filtered by tank-level parameter enablement FIRST (design
  // decision 6); the Add menu must never offer a tile whose parameter is
  // disabled at the tank level. While tankParams is still resolving we don't
  // yet know which parameters are enabled, so this is null and NO hidden tiles
  // are offered — defaulting to "all enabled" would transiently offer a tile
  // whose parameter turns out to be tank-disabled. Charts are unaffected.
  const enabledTileKeys = useMemo(() => {
    if (!tankParams?.parameters) return null;
    return new Set(tankParams.parameters.filter(p => p.is_enabled).map(p => p.key));
  }, [tankParams]);

  const hiddenItems: HiddenItem[] = useMemo(() => [
    ...(enabledTileKeys
      ? layout.tiles.hidden
          .filter(id => enabledTileKeys.has(id))
          .map(id => ({ section: 'tiles' as const, id, label: tileLabelFor(id) }))
      : []),
    ...layout.charts.hidden.map(id => ({
      section: 'charts' as const,
      id,
      label: chartLabelFor(id),
    })),
  ], [layout, enabledTileKeys]);

  const labelOf = (section: LayoutSection, id: string): string =>
    section === 'tiles' ? tileLabelFor(id) : chartLabelFor(id);

  const handleRemove = (section: LayoutSection, id: string): void => {
    // Capture the position BEFORE hide() mutates the order, so Undo can
    // restore the item at the captured position — exact for LIFO undo,
    // best-effort if the layout changed since removal (Add-menu restore
    // still appends).
    const removedAtIndex = layout[section].order.indexOf(id);
    hide(section, id);
    controlsRef.current?.focusDone(); // removed node unmounts; don't drop focus on <body>
    const label = labelOf(section, id);
    const toastId = `dashboard-remove-${section}-${id}`;
    const tankIdAtRemove = selectedTankIdRef.current;
    // Deterministic re-show: if this exact toast is still on screen (remove →
    // restore via Add menu → remove again within its autoClose window),
    // Mantine would ignore a show() with a duplicate id — hide first.
    notifications.hide(toastId);
    notifications.show({
      id: toastId,
      title: `${label} removed`,
      message: (
        <Button
          variant="subtle"
          size="compact-xs"
          onClick={() => {
            // The toast can outlive a tank switch; restoring would then
            // mutate the WRONG tank's layout. No-op unless still on the
            // tank the removal happened on.
            if (selectedTankIdRef.current === tankIdAtRemove) {
              // Restore at the captured position (indexOf misses yield -1,
              // which show() treats as append).
              show(section, id, removedAtIndex);
            }
            // Dismiss immediately: a clicked Undo shouldn't linger for the
            // rest of its autoClose window (and remove→Undo→remove-again
            // reuses this deterministic id within that window).
            notifications.hide(toastId);
          }}
        >
          Undo
        </Button>
      ),
      color: 'gray',
      autoClose: 5000,
    });
  };

  const handleRestore = (section: LayoutSection, id: string): void => {
    show(section, id);
    // Restoring the LAST hidden item unmounts the Add menu (and its focused
    // trigger) — always move focus to Done so it is never dropped on <body>.
    controlsRef.current?.focusDone();
    notifications.show({
      message: `${labelOf(section, id)} added to the end — drag to reposition`,
      color: 'gray',
      autoClose: 4000,
    });
  };

  // Ordered visible charts. Reorders map dragged ids into the CANONICAL
  // layout.charts.order (not visible-grid indices) — if chart ids are ever
  // filtered (feature flags, device availability) visible indices would
  // diverge from the canonical order exactly like disabled-parameter tiles.
  // indexOf misses return -1; move() no-ops on negative indices.
  const orderedCharts = useMemo(() => layout.charts.order
    .map(id => CHART_REGISTRY.find(c => c.id === id))
    .filter((c): c is (typeof CHART_REGISTRY)[number] => c !== undefined),
    [layout.charts.order]);

  const chartIds = useMemo(() => orderedCharts.map(c => c.id), [orderedCharts]);
  // chartLabelFor is module-scope (stable), so this memo never recomputes; it
  // just keeps the announcements object identity stable across renders.
  const chartAnnouncements = useMemo(() => buildDndAnnouncements(chartLabelFor), []);

  const handleChartDragEnd = (event: DragEndEvent): void => {
    setActiveChartId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const order = layout.charts.order;
    move('charts', order.indexOf(String(active.id)), order.indexOf(String(over.id)));
  };

  // Handle manual refresh with time period selection
  const handleRefresh = async (timePeriod: 'today' | 'week' | 'month') => {
    if (!selectedTank) {
      notifications.show({
        title: 'No Tank Selected',
        message: 'Please select a tank before refreshing data',
        color: 'orange',
        icon: <IconAlertCircle size={16} />,
      });
      return;
    }

    setSelectedTimePeriod(timePeriod);

    try {
      const response = await apiClient(
        buildApiUrl(`/api/refresh/tanks/${selectedTank.id}/refresh`),
        {
          method: 'POST',
          body: JSON.stringify({ time_period: timePeriod }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Handle "no devices configured" or "all disabled" cases with friendly message
        if (response.status === 400 && errorData.detail) {
          if (errorData.detail.includes('No devices configured') || errorData.detail.includes('All devices are disabled')) {
            notifications.show({
              title: errorData.detail.includes('disabled') ? 'Devices Disabled' : 'No Devices Configured',
              message: errorData.detail,
              color: 'yellow',
              icon: <IconAlertCircle size={16} />,
              autoClose: 10000,
            });
            return;
          }
        }
        throw new Error(errorData.detail || 'Failed to trigger refresh');
      }

      const data = await response.json();
      setRefreshJobId(data.job_id);
      setProgressModalOpened(true);
    } catch (error) {
      console.error('Refresh failed:', error);
      notifications.show({
        title: 'Refresh Failed',
        message: error instanceof Error ? error.message : 'Failed to trigger data refresh',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    }
  };

  // Check if date range might cause performance issues
  const daysDiff = Math.floor(
    (dateRange[1].getTime() - dateRange[0].getTime()) / (1000 * 60 * 60 * 24)
  );

  const showPerformanceWarning = daysDiff > 90;

  // Handlers for annotation modal
  const handleAddNote = (
    parameter: string,
    parameterLabel: string,
    sources?: string[]
  ) => {
    setAnnotationChartContext({ parameter, parameterLabel, sources });
    setSelectedAnnotation(null);
    setAnnotationModalOpened(true);
  };

  const handleEditAnnotation = (annotation: ChartAnnotation) => {
    setAnnotationChartContext({
      parameter: annotation.parameter || '',
      parameterLabel: annotation.parameter || 'All Charts',
      sources: undefined,
    });
    setSelectedAnnotation(annotation);
    setAnnotationModalOpened(true);
  };

  return (
    <>
      <RefreshProgressModal
        jobId={refreshJobId}
        opened={progressModalOpened}
        onClose={() => {
          setProgressModalOpened(false);
          setRefreshJobId(null);
        }}
        onRefreshComplete={() => {
          queryClient.invalidateQueries({ queryKey: ['dashboard'] });
          queryClient.invalidateQueries({ queryKey: ['sparkline-data'] });
        }}
        timePeriod={selectedTimePeriod}
      />

      <Container fluid px={{ base: 'xs', sm: 'md', lg: 'xl' }}>
        <Stack gap="lg">
          <Group justify="space-between">
            <Title order={1}>Dashboard</Title>
            <Group gap="xs">
              {/* Customize only renders when the dashboard is actually
                  customizable: in the zero-data state charts are unmounted and
                  the tile grid (if kept) only shows its EmptyState, so edit
                  mode would have nothing to edit and Add-menu restores would
                  be invisible. */}
              {!isZeroData && (
                <DashboardEditControls
                  editMode={editMode}
                  onToggleEditMode={toggleEditMode}
                  hiddenItems={hiddenItems}
                  onRestore={handleRestore}
                  onReset={reset}
                  isCustomized={isCustomized}
                  controlsRef={controlsRef}
                />
              )}
              <Tooltip label="Export dashboard as PNG" position="left" withArrow>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="lg"
                  onClick={handleExportDashboard}
                  loading={isExporting}
                  aria-label="Export Dashboard"
                >
                  <IconDownload size={16} />
                </ActionIcon>
              </Tooltip>
              {/* Desktop: full refresh button with dropdown */}
              <Box visibleFrom="sm">
                <RefreshButton onRefresh={handleRefresh} />
              </Box>
              {/* Mobile: icon-only refresh with dropdown for date range */}
              <Box hiddenFrom="sm">
                <RefreshButton onRefresh={handleRefresh} iconOnly />
              </Box>
            </Group>
          </Group>

        {/* Getting Started checklist - guides new users; self-manages its own
            dismissal, celebration, and readiness visibility. Rendered OUTSIDE
            the dashboardRef export div so PNG exports never capture it. */}
        <GettingStartedCard />

        <div ref={dashboardRef}>
        <Stack gap="lg">
        {/* !isZeroData mirrors the Customize-control gate above: the effect
            that forces editMode off runs post-render, so without this gate the
            hint could flash for one frame when the zero-data signal settles
            mid-edit. */}
        {editMode && !isZeroData && (
          <Text size="sm" c="dimmed">
            Drag items to reorder, or press ✕ to remove. Removed items can be added back from the Add menu.
          </Text>
        )}

        {/* Metric Tiles - conditionally render based on enabled parameters.
            Hidden in the tank-with-no-data case: the GettingStartedCard hero
            above stands in for the dead N/A wall. */}
        {!hideTileGrid && (
          <MetricTilesGrid
            latestData={latestMetrics}
            targetRangesData={targetRanges}
            isLoading={dashboardLoading}
            error={dashboardError ?? undefined}
            parameters={tankParams?.parameters}
            features={tankParams?.features}
            sparklineData={sparklineData}
            tileOrder={layout.tiles.order}
            editMode={editMode}
            onReorder={(activeId, overId) => {
              // Map dragged ids into the CANONICAL tile order — never the
              // visible-grid indices, which diverge when a parameter is disabled.
              const order = layout.tiles.order;
              move('tiles', order.indexOf(activeId), order.indexOf(overId));
            }}
            onRemoveTile={id => handleRemove('tiles', id)}
          />
        )}

        {/* Tasks Section - only show when tank is selected */}
        {selectedTank && (
          <ErrorBoundary
            fallback={<Alert color="red">Tasks section failed to load</Alert>}
          >
            <DashboardTasks />
          </ErrorBoundary>
        )}

        {/* Charts - hidden entirely in the zero-data state so a brand-new
            dashboard never shows a stack of "No data available" alerts. */}
        {!isZeroData && (
        <>
        {showPerformanceWarning && (
          <Alert color="yellow" title="Large Date Range">
            Data will be downsampled for ranges over 90 days to maintain performance.
          </Alert>
        )}

        <ChartControls
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          showMovingAverage={showMovingAverage}
          onMovingAverageChange={setShowMovingAverage}
        />

        {/* Charts in 2-column grid on desktop, 1-column on mobile */}
        {orderedCharts.length === 0 && <AllHiddenNotice itemNoun="charts" />}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          accessibility={{ announcements: chartAnnouncements }}
          onDragStart={e => setActiveChartId(String(e.active.id))}
          onDragCancel={() => setActiveChartId(null)}
          onDragEnd={handleChartDragEnd}
        >
          <SortableContext items={chartIds} strategy={rectSortingStrategy}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: 'var(--mantine-spacing-lg)',
            }}>
              {orderedCharts.map(chart => (
                <div
                  key={chart.id}
                  style={{ gridColumn: expandedCharts[chart.id] ? '1 / -1' : undefined }}
                >
                  <SortableItem
                    id={chart.id}
                    editMode={editMode}
                    onRemove={() => handleRemove('charts', chart.id)}
                    removeLabel={chartLabelFor(chart.id)}
                  >
                    <ErrorBoundary
                      fallback={<Alert color="red">{chart.label} chart failed to load</Alert>}
                    >
                      {chart.render({
                        dateRange,
                        dashboardData,
                        isLoading: dashboardLoading,
                        error: dashboardError ?? undefined,
                        annotations,
                        expanded: !!expandedCharts[chart.id],
                        onToggle: () => toggleChart(chart.id),
                        onAddNote: handleAddNote,
                        onAnnotationClick: handleEditAnnotation,
                      })}
                    </ErrorBoundary>
                  </SortableItem>
                </div>
              ))}
            </div>
          </SortableContext>
          <DragOverlay>
            {activeChartId ? (
              <Paper p="md" withBorder style={{ transform: 'scale(1.02)' }}>
                <Text fw={500}>{chartLabelFor(activeChartId)}</Text>
              </Paper>
            ) : null}
          </DragOverlay>
        </DndContext>
        </>
        )}

        </Stack>
        </div>
      </Stack>
    </Container>

    <AddAnnotationModal
      opened={annotationModalOpened}
      onClose={() => {
        setAnnotationModalOpened(false);
        setSelectedAnnotation(null);
      }}
      tankId={selectedTank?.id}
      chartContext={annotationChartContext || { parameter: '', parameterLabel: '' }}
      existingAnnotation={selectedAnnotation}
    />
    </>
  );
}
